import { StackActions, useNavigation } from '@react-navigation/native';
import { useCallback } from 'react';

import type { NavigationAction } from '@react-navigation/native';

/**
 * Minimal shape of a navigator state needed to decide how to go back.
 * Keeping it structural (instead of importing NavigationState) lets the
 * pure decision function be unit-tested without a navigator.
 */
export type ReturnToSourceState = {
  /** Key of the navigator the action must stay inside of. */
  key: string;
  /** Index of the focused route within that navigator. */
  index: number;
  /** Navigator kind, e.g. 'stack' | 'tab' | 'drawer'. */
  type?: string;
};

/**
 * Decides the back action for a screen that can be reached from more than
 * one pushed stack.
 *
 * `StackActions.popToTop()` dispatched without a target bubbles upward until
 * some navigator can handle it. For a nested stack that is only one screen
 * deep (e.g. projects-stack pushed from chats-stack), nothing can be popped
 * locally, so the action escapes to the root stack and resets the app back to
 * the tab navigator — skipping the screen the user actually came from
 * (TA-10492, TA-10514).
 *
 * - Stack navigator, deeper than the first route: pop to the top of the
 *   *local* stack, with an explicit `target` so the action can never bubble
 *   past it.
 * - On the first route: there is nothing local to pop, so the caller should
 *   `goBack()` where it can — a single pop of the parent stack, matching
 *   swipe-back. On a root screen with nothing to go back to, doing nothing is
 *   the correct outcome.
 * - Any other navigator kind (tab, drawer): a targeted POP_TO_TOP would be
 *   dropped on the floor by a navigator that cannot handle it, leaving the
 *   back control dead, so fall back to `goBack()` as well.
 *
 * @param state Local navigator state (`navigation.getState()`).
 * @returns The scoped popToTop action, or `null` when the caller should
 *   `goBack()` instead.
 */
export const returnToSourceAction = (
  state: ReturnToSourceState | undefined
): (NavigationAction & { target: string }) | null => {
  if (!state || state.type !== 'stack' || state.index <= 0) return null;

  return { ...StackActions.popToTop(), target: state.key };
};

/**
 * Returns a back handler that never resets the app: it pops to the top of the
 * local stack when there is something to pop, and otherwise falls back to
 * `goBack()` when the navigator can go back (on a root screen it is a no-op).
 *
 * Use this instead of `navigation.dispatch(StackActions.popToTop())` for any
 * screen reachable from multiple pushed stacks.
 */
export const useReturnToSource = () => {
  const navigation = useNavigation();

  return useCallback(() => {
    const action = returnToSourceAction(navigation.getState?.());

    if (action) {
      navigation.dispatch(action);
      return;
    }

    if (navigation.canGoBack()) navigation.goBack();
  }, [navigation]);
};
